import { useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';

import { GET_ACTIVE_ROUTE_SEGMENT } from '../gql/queries/surveillanceRoute';
import { getQueue } from '../offline/offlineQueue';
import {
  deliveriesToResume,
  forgetRouteDelivery,
  resumeActionFor,
} from '../offline/pendingRouteDelivery';
import { showSuccessToast } from '../utils/toasts';

type FinishRoute = (
  input: { id: string; registerEndDate?: string },
  opts?: { silentSuccess?: boolean }
) => Promise<unknown>;

/** Una sola pasada a la vez (montaje, `online` y fin de sincronización). */
let resuming = false;

/**
 * Completa las entregas de «Guardar y entregar» que una recarga dejó a medias
 * (P2, QA T6b del 15-oct). Ver `src/offline/pendingRouteDelivery.ts`.
 *
 * Se intenta al montar (la recarga), al volver la red y al acabar de
 * sincronizar la cola (lo encolado del recorrido ya se ha aplicado). Para cada
 * intención pregunta al servidor si el tramo sigue abierto y decide con
 * `resumeActionFor`: entregar, esperar a la cola o tirarla. Sin red la pregunta
 * falla y la intención se queda para la siguiente vez.
 */
export function useResumeRouteDelivery({
  enabled,
  userId,
  finishRoute,
}: {
  enabled: boolean;
  userId?: string | null;
  finishRoute: FinishRoute;
}): void {
  const client = useApolloClient();
  const { t } = useTranslation(['modals', 'toasts']);
  // El `finishRoute` del hook cambia en cada render: el efecto no debe.
  const finishRouteRef = useRef(finishRoute);
  finishRouteRef.current = finishRoute;
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    if (!enabled) return;

    const run = async () => {
      if (resuming) return;
      resuming = true;
      try {
        for (const entry of deliveriesToResume(userId)) {
          let serverActiveSegmentId: string | null;
          try {
            const { data } = await client.query({
              query: GET_ACTIVE_ROUTE_SEGMENT,
              variables: { surveillanceRouteId: entry.routeId },
              fetchPolicy: 'network-only',
            });
            serverActiveSegmentId = data?.activeRouteSegment?.id ?? null;
          } catch {
            // Sin red (o el servidor no responde): se retoma más tarde.
            continue;
          }
          const action = resumeActionFor(
            entry,
            serverActiveSegmentId,
            await getQueue()
          );
          if (action === 'wait') continue;
          forgetRouteDelivery(entry.routeId);
          if (action === 'drop') continue;
          try {
            const delivered = await finishRouteRef.current(
              { id: entry.routeId, registerEndDate: entry.registerEndDate },
              { silentSuccess: true }
            );
            showSuccessToast(
              tRef.current(
                delivered === null
                  ? 'modals:changeState.queuedOffline'
                  : 'toasts:success.updated'
              )
            );
          } catch (error) {
            // El aviso de error ya lo enseña `finishRoute`.
            Sentry.captureException(error, {
              tags: {
                feature: 'surveillance-route',
                action: 'resumeRouteDelivery',
              },
            });
          }
        }
      } finally {
        resuming = false;
      }
    };

    void run();
    window.addEventListener('online', run);
    window.addEventListener('offline-sync-complete', run);
    return () => {
      window.removeEventListener('online', run);
      window.removeEventListener('offline-sync-complete', run);
    };
  }, [enabled, userId, client]);
}

export default useResumeRouteDelivery;
